// Core enums

export type PriorityLevel = 'high' | 'medium' | 'low';
export type TargetAudience = 'loan_officer' | 'admin' | 'va';
export type UserRole = 'admin' | 'va' | 'loan_officer';
export type StepStatus = 'not_started' | 'in_progress' | 'blocked' | 'completed' | 'skipped';
export type ActivityType =
  | 'task'
  | 'form'
  | 'document_upload'
  | 'training'
  | 'meeting'
  | 'call'
  | 'payment'
  | 'signature'
  | 'video'
  | 'custom';

// Catalog tables

export interface StepCategoryRecord {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  color: string;
  accent_color: string;
  icon: string;
  sequence_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Input configuration for data collection steps
export type InputConfigType = 'w9' | 'banking' | 'phone_input' | 'text_input' | 'file_upload';
export type ResourceType = 'email_signature';

export interface InputConfig {
  type?: InputConfigType;
  label?: string;
  placeholder?: string;
  saveTo?: string; // e.g. "profiles.phone"
  required?: boolean;
  accept?: string; // for file uploads, e.g. ".pdf"
  showResource?: ResourceType; // show download button for this resource
  downloadUrl?: string; // URL to download blank form (for file_upload type)
  documentType?: string; // type of document for storage folder (e.g. "w9", "banking")
}

export interface OnboardingStep {
  id: string;
  category: string; // references step_categories.slug
  task_name: string;
  sequential_order: number;
  priority: PriorityLevel;
  target_audience: TargetAudience;
  activity_type: ActivityType;
  notes: string | null;
  dependencies: string[] | null;
  required_flags: string[] | null; // flags required for task visibility
  input_config: InputConfig | null; // configuration for data collection
  notify_role_on_complete: 'admin' | 'va' | 'both' | 'none' | null;
  section: string; // 'onboarding' | 'hands_on'
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Progress tables

export interface LoanOfficerProgress {
  id: string;
  loan_officer_id: string;
  step_id: string;
  status: StepStatus;
  assigned_date: string;
  started_date: string | null;
  completed_date: string | null;
  completed_by: string | null;
  notes: string | null;
  blocked_reason: string | null;
  documents: DocumentMetadata[];
  created_at: string;
  updated_at: string;
  step?: OnboardingStep;
}

export interface CustomTask {
  id: string;
  loan_officer_id: string;
  title: string;
  description: string | null;
  priority: PriorityLevel;
  status: StepStatus;
  due_date: string | null;
  assigned_by: string;
  assigned_date: string;
  completed_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivityLog {
  id: string;
  loan_officer_id: string | null;
  actor_id: string | null;
  actor_type: string | null;
  action_type: string;
  step_id: string | null;
  task_id: string | null;
  details: Record<string, any> | null;
  created_at: string;
  step?: OnboardingStep;
  task?: CustomTask;
}

export interface DocumentMetadata {
  id: string;
  name: string;
  type: string;
  url: string;
  uploaded_at: string;
}

// Views

export interface LOProgressSummary {
  loan_officer_id: string;
  total_steps: number;
  completed_steps: number;
  in_progress_steps: number;
  blocked_steps: number;
  not_started_steps: number;
  completion_percentage: number;
  onboarding_start_date: string;
  latest_completion_date: string | null;
}

export interface LOCategoryProgress {
  loan_officer_id: string;
  category: string;
  category_name: string;
  category_color: string;
  sequence_order: number;
  total_steps: number;
  completed_steps: number;
  blocked_steps: number;
  completion_percentage: number;
}

export interface LONextStep {
  loan_officer_id: string;
  step_id: string;
  task_name: string;
  category: string;
  sequential_order: number;
  priority: PriorityLevel;
  status: StepStatus;
}

// Extended relations

export interface ProgressWithStep extends LoanOfficerProgress {
  step: OnboardingStep;
}

export interface LoanOfficer {
  id: string;
  email: string;
  personal_email?: string | null;
  first_name: string;
  last_name: string;
  full_name?: string | null;
  user_role: string;
  location_id?: string | null;
  headshot?: string | null;
  phone?: string | null;
  cell_number?: string | null;
  office_address?: string | null;
  title?: string | null;
  nmls_number?: string | null;
  nmls?: string | number | null;
  dre_number?: string | null;
  linkedin_url?: string | null;
  website_url?: string | null;
  email_signature_url?: string | null;
  profile_flags?: Record<string, boolean> | null; // Conditional task visibility flags
  must_change_password?: boolean;
  place_of_work?: PlaceOfWork | null;
  state_licenses?: string[] | null;
  created_at: string;
}

export interface LoanOfficerWithProgress extends LoanOfficer {
  progress_summary?: LOProgressSummary;
  progress?: ProgressWithStep[];
  custom_tasks?: CustomTask[];
}

// UI helper types

export interface CategoryGroup {
  category: StepCategoryRecord;
  steps: ProgressWithStep[];
  completed: number;
  total: number;
}

export interface DashboardStats {
  total_steps: number;
  completed_steps: number;
  in_progress_steps: number;
  blocked_steps: number;
  completion_percentage: number;
  next_step?: ProgressWithStep;
  pending_tasks: number;
  overdue_tasks: number;
}

// Admin

export interface AdminDashboardStats {
  total_loan_officers: number;
  active_onboarding: number;
  completed_onboarding: number;
  average_completion: number;
  total_blocked_steps: number;
  recent_completions: number;
}

export interface LOListItem {
  id: string;
  name: string;
  email: string;
  start_date: string;
  completion_percentage: number;
  completed_steps: number;
  total_steps: number;
  blocked_steps: number;
  status: StepStatus;
}

// A2P registration + admin helper types

export type A2PStatus = 'not_started' | 'pending' | 'failed' | 'approved';

export interface A2PRegistration {
  id: string;
  loan_officer_id: string;
  brand_status: A2PStatus;
  brand_reference_id: string | null;
  brand_submitted_at: string | null;
  brand_approved_at: string | null;
  brand_failed_at: string | null;
  brand_failure_reason: string | null;
  brand_notes: string | null;
  campaign_status: A2PStatus;
  campaign_reference_id: string | null;
  campaign_submitted_at: string | null;
  campaign_approved_at: string | null;
  campaign_failed_at: string | null;
  campaign_failure_reason: string | null;
  campaign_notes: string | null;
  phone_number: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface A2PStatusHistory {
  id: string;
  a2p_registration_id: string;
  registration_type: 'brand' | 'campaign';
  old_status: A2PStatus | null;
  new_status: A2PStatus;
  reference_id: string | null;
  failure_reason: string | null;
  notes: string | null;
  webhook_payload: Record<string, any> | null;
  changed_by: string | null;
  changed_at: string;
}

export interface A2PRegistrationSummary extends A2PRegistration {
  first_name: string;
  last_name: string;
  email: string;
  overall_status: 'not_started' | 'in_progress' | 'pending' | 'failed' | 'completed';
}

export interface A2PBusinessRegistration {
  id: string;
  loan_officer_id: string;
  business_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  ein: string | null;
  irs_paperwork: string | null;
  brand_approved: boolean | null;
  campaign_approved: boolean | null;
  created_at: string;
}

// Visual helpers

export const priorityLabels: Record<PriorityLevel, string> = {
  high: 'High Priority',
  medium: 'Medium Priority',
  low: 'Low Priority',
};

export const statusLabels: Record<StepStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  completed: 'Completed',
  skipped: 'Skipped',
};

export const statusColors: Record<StepStatus, string> = {
  not_started: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-[#e0f2ff] text-lumin-primary',
  blocked: 'bg-[#fff4e5] text-amber-700',
  completed: 'bg-[#e8fdf4] text-emerald-700',
  skipped: 'bg-slate-100 text-slate-500',
};

export const priorityColors: Record<PriorityLevel, string> = {
  high: 'bg-[#ffe4e6] text-rose-700',
  medium: 'bg-[#fff4e5] text-amber-700',
  low: 'bg-[#e8fdf4] text-emerald-700',
};

export const activityTypeCopy: Record<ActivityType, { label: string; description: string }> = {
  task: { label: 'Task', description: 'General checklist item or admin action' },
  form: { label: 'Form', description: 'Internal or public form submission' },
  document_upload: { label: 'Upload', description: 'Collect documents or assets' },
  training: { label: 'Training', description: 'Courses, videos, or live sessions' },
  meeting: { label: 'Meeting', description: 'Schedule or attend live meeting' },
  call: { label: 'Call', description: 'Phone or video call touchpoint' },
  payment: { label: 'Payment', description: 'Fees, subscriptions, or billing' },
  signature: { label: 'Signature', description: 'Needs e-sign or approval' },
  video: { label: 'Video', description: 'Watch and acknowledge a video' },
  custom: { label: 'Custom', description: 'Custom workflow trigger' },
};

export const a2pStatusLabels: Record<A2PStatus, string> = {
  not_started: 'Not Started',
  pending: 'Pending',
  failed: 'Failed',
  approved: 'Approved',
};

export const a2pStatusColors: Record<A2PStatus, string> = {
  not_started: 'bg-slate-100 text-slate-600 border-slate-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  failed: 'bg-rose-50 text-rose-700 border-rose-100',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
};

export const getOnboardingStatus = (
  progress?: LOProgressSummary
): 'not_started' | 'in_progress' | 'blocked' | 'completed' => {
  if (!progress) return 'not_started';

  if (progress.completion_percentage === 100) return 'completed';
  if (progress.blocked_steps > 0) return 'blocked';
  if (progress.in_progress_steps > 0 || progress.completed_steps > 0) return 'in_progress';
  return 'not_started';
};

// Parse a date string as local time (handles both date-only "2026-01-05" and full timestamps)
export const parseLocalDate = (dateString: string): Date => {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC by default, causing timezone issues
  // Add T00:00:00 to force local time parsing for date-only strings
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(dateString + 'T00:00:00');
  }
  return new Date(dateString);
};

// Format a date for display (handles timezone correctly for date-only strings)
export const formatLocalDate = (
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
): string => {
  if (!dateString) return 'N/A';
  return parseLocalDate(dateString).toLocaleDateString('en-US', options);
};

export const getDaysSince = (dateString: string): number => {
  const date = parseLocalDate(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export const formatRelativeDate = (dateString?: string | null): string => {
  if (!dateString) return '—';
  const days = getDaysSince(dateString);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

// Format phone number to consistent (XXX) XXX-XXXX format
export const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '—';

  // Strip all non-numeric characters
  const digits = phone.replace(/\D/g, '');

  // Handle 11-digit numbers starting with 1 (US country code)
  const normalized = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;

  // Format 10-digit US numbers
  if (normalized.length === 10) {
    return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
  }

  // Return original if not a standard US number
  return phone;
};

// Prospect system

export type ProspectStatus =
  | 'recruit'
  | 'pending'
  | 'email_created'
  | 'onboarding_sent'
  | 'form_submitted'
  | 'paid'
  | 'converted';

export type ProspectTitle = 'Sr Mortgage Broker' | 'Executive Branch Manager';

export type PlaceOfWork = 'Remote' | 'In Office';

// Available state licenses from Lumin Lending company_licenses table
export const COMPANY_LICENSES = [
  'Alabama',
  'Arizona',
  'California',
  'Colorado',
  'Idaho',
  'Illinois',
  'Kansas',
  'Maryland',
  'Michigan',
  'New Mexico',
  'Ohio',
  'Oregon',
  'Pennsylvania',
  'Tennessee',
  'Texas',
  'Utah',
  'Washington',
  'Wyoming',
] as const;

export type StateLicense = typeof COMPANY_LICENSES[number];

// State name to abbreviation mapping
export const STATE_ABBREVIATIONS: Record<string, string> = {
  'Alabama': 'AL',
  'Arizona': 'AZ',
  'California': 'CA',
  'Colorado': 'CO',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Kansas': 'KS',
  'Maryland': 'MD',
  'Michigan': 'MI',
  'New Mexico': 'NM',
  'Ohio': 'OH',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Washington': 'WA',
  'Wyoming': 'WY',
};

export interface Prospect {
  id: string;
  first_name: string;
  last_name: string;
  preferred_first_name: string | null;
  title: ProspectTitle;
  phone: string | null;
  email: string | null;
  nmls: string | null;
  place_of_work: PlaceOfWork | null;
  expected_month: string | null;
  expected_start_date: string | null;
  company_email: string | null;
  company_email_created_at: string | null;
  state_licenses: string[] | null;
  status: ProspectStatus;
  loan_officer_id: number | null;
  created_by: number;
  recruiter_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProspectOnboardingData {
  id: string;
  prospect_id: string;
  token: string;
  token_expires_at: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_first_name: string | null;
  cell_phone: string | null;
  personal_email: string | null;
  date_of_birth: string | null;
  nmls: string | null;
  ca_dre: string | null;
  languages_spoken: string | null;
  ssn_last_four: string | null;
  home_address: string | null;
  home_city: string | null;
  home_state: string | null;
  home_zip: string | null;
  business_ein: string | null;
  business_legal_name: string | null;
  business_type: string | null;
  business_address: string | null;
  business_city: string | null;
  business_state: string | null;
  business_zip: string | null;
  irs_letter_url: string | null;
  headshot_url: string | null;
  family_picture_url: string | null;
  place_of_work: PlaceOfWork | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_payment_status: string | null;
  form_completed_at: string | null;
  payment_completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export const prospectStatusLabels: Record<ProspectStatus, string> = {
  recruit: 'Recruit',
  pending: 'Pending',
  email_created: 'Email Created',
  onboarding_sent: 'Email Sent',
  form_submitted: 'Form Submitted',
  paid: 'Paid',
  converted: 'Converted to LO',
};

export const prospectStatusColors: Record<ProspectStatus, string> = {
  recruit: 'bg-amber-100 text-amber-800',
  pending: 'bg-gray-100 text-gray-800',
  email_created: 'bg-blue-100 text-blue-800',
  onboarding_sent: 'bg-purple-100 text-purple-800',
  form_submitted: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  converted: 'bg-teal-100 text-teal-800',
};

// CEO Timeline - tracks onboarding milestone dates
export interface TimelineEntry {
  id: string;
  prospect_id: string;
  loan_officer_id: string | null;
  unsponsor_date: string | null;
  sponsor_date: string | null;
  training_date: string | null;
  leads_live_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  prospect: {
    id: string;
    first_name: string;
    last_name: string;
    title: string;
    phone: string | null;
    email: string | null;
    nmls: string | null;
    status: ProspectStatus;
    company_email: string | null;
    expected_start_date: string | null;
    onboarding_data: {
      ca_dre: string | null;
      nmls: string | null;
      cell_phone: string | null;
      form_completed_at: string | null;
    } | null;
  };
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    cell_number: string | null;
    email: string | null;
    nmls_number: string | null;
    dre_number: string | null;
  } | null;
}

// Scheduled Edge Function Calls (for scheduled emails)
export interface ScheduledEmailCall {
  id: string;
  function_name: string;
  payload: { prospect_id: string };
  execute_after: string;
  max_retries: number;
  retry_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  last_attempt_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  prospect_id: string | null;
  loan_officer_id: string | null;
  created_at: string;
  updated_at: string;
}

// W9 Submission
export interface W9Submission {
  id: string;
  loan_officer_id: number;
  name: string;
  business_name: string | null;
  tax_classification: string;
  llc_tax_classification: string | null;
  other_classification: string | null;
  exempt_payee_code: string | null;
  exemption_from_fatca_code: string | null;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  ssn_or_ein: string;
  pdf_url: string;
  submitted_at: string | null;
  created_at: string;
}

// Banking Information
export interface BankingInformation {
  id: string;
  loan_officer_id: number;
  account_holder_name: string;
  bank_name: string;
  routing_number: string;
  account_number: string;
  account_type: 'checking' | 'savings';
  quickbooks_vendor_id: string | null;
  quickbooks_synced_at: string | null;
  submitted_at: string | null;
  created_at: string;
}

// Tax classification options for W9
export const taxClassificationOptions = [
  { value: 'individual', label: 'Individual/sole proprietor or single-member LLC' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'trust_estate', label: 'Trust/estate' },
  { value: 'llc', label: 'Limited liability company (see instructions)' },
  { value: 'other', label: 'Other' },
];

// =============================================================================
// Notifications & Comments System
// =============================================================================

export type NotificationType =
  | 'step_completed'
  | 'task_completed'
  | 'prospect_status'
  | 'manual_handoff'
  | 'comment_added'
  | 'task_assigned';

export type NotifyRole = 'admin' | 'va' | 'both' | 'none';

export type NotificationTriggerType =
  | 'step_completed'
  | 'task_completed'
  | 'prospect_status_change'
  | 'task_assigned'
  | 'timeline_stage_change'
  | 'manual_handoff';

export interface NotificationRule {
  id: string;
  name: string;
  description: string | null;
  trigger_type: NotificationTriggerType;
  trigger_step_id: string | null;
  trigger_task_pattern: string | null;
  trigger_prospect_status: string | null;
  trigger_timeline_field: string | null;
  trigger_handoff_to_role: 'admin' | 'va' | null;
  notify_role: 'admin' | 'va' | 'both';
  send_email: boolean;
  notify_emails: string[];
  cc_emails: string[];
  bcc_emails: string[];
  email_template_slug: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  recipient_id: string;
  recipient_role: 'admin' | 'va';
  type: NotificationType;
  rule_id: string | null;
  loan_officer_id: string | null;
  prospect_id: string | null;
  step_id: string | null;
  progress_id: string | null;
  task_id: string | null;
  comment_id: string | null;
  title: string;
  message: string | null;
  sender_id: string | null;
  sender_name: string | null;
  is_read: boolean;
  read_at: string | null;
  is_email_sent: boolean;
  email_sent_at: string | null;
  metadata: Record<string, any>;
  created_at: string;
  // Joined fields
  loan_officer?: LoanOfficer;
  prospect?: Prospect;
}

export interface LOComment {
  id: string;
  loan_officer_id: string;
  author_id: string;
  author_name: string;
  author_role: 'admin' | 'va';
  parent_id: string | null;
  content: string;
  attachments: Array<{ name: string; url: string; type: string }>;
  mentions: string[];
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // For threading
  replies?: LOComment[];
}

export interface Handoff {
  id: string;
  from_user_id: string;
  from_role: 'admin' | 'va';
  to_role: 'admin' | 'va';
  to_user_id: string | null;
  loan_officer_id: string | null;
  prospect_id: string | null;
  progress_id: string | null;
  message: string | null;
  is_acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
  // Joined fields
  from_user?: { first_name: string; last_name: string };
  loan_officer?: LoanOfficer;
  prospect?: Prospect;
}

export const notificationTypeLabels: Record<NotificationType, string> = {
  step_completed: 'Step Completed',
  task_completed: 'Task Completed',
  prospect_status: 'Prospect Update',
  manual_handoff: 'Handoff',
  comment_added: 'New Comment',
  task_assigned: 'Task Assigned',
};

export const notificationTypeColors: Record<NotificationType, string> = {
  step_completed: 'bg-emerald-100 text-emerald-700',
  task_completed: 'bg-blue-100 text-blue-700',
  prospect_status: 'bg-purple-100 text-purple-700',
  manual_handoff: 'bg-amber-100 text-amber-700',
  comment_added: 'bg-slate-100 text-slate-700',
  task_assigned: 'bg-indigo-100 text-indigo-700',
};

export const notifyRoleLabels: Record<NotifyRole, string> = {
  admin: 'Notify Admin',
  va: 'Notify VA',
  both: 'Notify Both',
  none: 'No Notification',
};

export const triggerTypeLabels: Record<NotificationTriggerType, string> = {
  step_completed: 'When step is completed',
  task_completed: 'When task is completed',
  prospect_status_change: 'When prospect status changes',
  task_assigned: 'When task is assigned',
  timeline_stage_change: 'When timeline stage changes',
  manual_handoff: 'When handoff is created',
};

export const timelineFieldLabels: Record<string, string> = {
  sponsor_date: 'Sponsor Date',
  training_date: 'Training Date',
  leads_live_date: 'Leads Live Date',
  unsponsor_date: 'Unsponsor Date',
};

export const handoffToRoleLabels: Record<string, string> = {
  admin: 'Handoff to Admin Team',
  va: 'Handoff to VA Team',
};

// State Sponsorship types
export type SponsorshipStatus = 'pending' | 'submitted' | 'accepted' | 'rejected';

export interface StateSponsorship {
  id: string;
  loan_officer_id: string;
  state_code: string;
  state_name: string;
  status: SponsorshipStatus;
  nmls_id: string | null;
  company_nmls_id: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export const sponsorshipStatusLabels: Record<SponsorshipStatus, string> = {
  pending: 'Pending',
  submitted: 'Submitted',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

export const sponsorshipStatusColors: Record<SponsorshipStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};
